import type { HeadlineResponse } from '@on3/api';
import { useSubscribe } from '@on3/ui-lib/components/Subscription/Provider';
import React, {
  type Context,
  createContext,
  type SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const ArticleContext: Context<IArticleContext> = createContext(
  {} as IArticleContext,
);

interface IArticleContext {
  article?: HeadlineResponse | null;
  setArticle: (
    value: SetStateAction<HeadlineResponse | null | undefined>,
  ) => void;
}
interface IArticleContextProps {
  article?: HeadlineResponse | null;
  children: React.ReactNode;
}

export const ArticleProvider = ({
  children,
  article: headline,
  ...props
}: IArticleContextProps) => {
  const { isSubscribed } = useSubscribe();

  const [article, setArticle] = useState<HeadlineResponse | null | undefined>(
    headline,
  );

  useEffect(() => {
    if (!isSubscribed) {
      sessionStorage?.setItem(
        'content_ref_key',
        JSON.stringify(article?.key || null),
      );

      sessionStorage?.setItem(
        'author_ref_key',
        JSON.stringify(article?.author?.key || null),
      );
    }
  }, [article, isSubscribed]);

  useEffect(() => {
    setArticle(headline);
  }, [headline]);

  const contextValue = useMemo(
    () => ({
      article,
      setArticle,
    }),
    [article, setArticle],
  );

  return (
    <ArticleContext.Provider value={contextValue}>
      {children}
    </ArticleContext.Provider>
  );
};

const useArticle = () => {
  const context = useContext(ArticleContext);

  return context;
};

export { useArticle };
